import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Heart } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { favoritesApi } from '../../lib/api';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';

// Heart-shaped favorite toggle, shared by every Article/How-It-Works card and detail
// page (see UserFavorite's docstring in backend/db/models.py for the 3 content_type
// values this can take). Logged-in click: optimistic toggle + a confirmation toast,
// rolled back with an error toast on API failure. Logged-out click: never calls the
// API -- shows a toast prompting login instead, and the icon stays unfavorited.
//
// `initialFavorited` is normally seeded by the parent page's batch useFavoritedIds()
// check, which resolves asynchronously after this button's first render -- the effect
// below re-syncs local state when that arrives.
//
// `onChange(nextFavorited)` is optional -- fired right after each optimistic update
// (and again on rollback), for a caller whose list IS the favorited set (the
// Dashboard) to remove an item the moment it's unfavorited, not just re-render its icon.
const FavoriteButton = ({ contentType, contentId, title, initialFavorited = false, className, iconClassName, size = 'icon', onChange }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [favorited, setFavorited] = useState(initialFavorited);
  const [pulsing, setPulsing] = useState(false);

  useEffect(() => {
    setFavorited(initialFavorited);
  }, [initialFavorited]);

  const handleClick = async (e) => {
    // Most callers render this inside a card <Link> -- stop the click from also
    // navigating or bubbling to the card.
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      toast('Log in to mark favorites', {
        description: 'Create a free account or log in to save this for later.',
        action: {
          label: 'Log In',
          onClick: () => navigate('/login', { state: { from: location.pathname } }),
        },
      });
      return;
    }

    const next = !favorited;
    setFavorited(next);
    onChange?.(next);
    setPulsing(true);
    window.setTimeout(() => setPulsing(false), 300);

    try {
      if (next) {
        await favoritesApi.add(contentType, contentId);
      } else {
        await favoritesApi.remove(contentType, contentId);
      }
      toast.success(next ? 'Added to Favorites' : 'Removed from Favorites');
    } catch (err) {
      console.error('Error updating favorite:', err);
      setFavorited(!next); // rollback the optimistic update
      onChange?.(!next);
      toast.error('Failed to update favorites. Please try again.');
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size={size}
      onClick={handleClick}
      className={cn('rounded-full hover:bg-red-50 shrink-0', className)}
      aria-pressed={favorited}
      aria-label={favorited ? `Remove ${title || 'item'} from favorites` : `Add ${title || 'item'} to favorites`}
      data-testid={`favorite-btn-${contentType}-${contentId}`}
    >
      <Heart
        className={cn(
          'transition-transform duration-200',
          pulsing && 'scale-125',
          favorited ? 'fill-red-500 text-red-500' : (iconClassName || 'text-muted-foreground')
        )}
      />
    </Button>
  );
};

export default FavoriteButton;
